<template>
  <div :class="classList" class="quantity-input">
    <button
      :disabled="disabled"
      type="button"
      class="quantity-input__button quantity-input__button--left"
      :class="{ 'quantity-input__button--light': inputValue === min }"
      @click="onQuantityChange('subtract')"
    >
      <IconMinusCircle v-if="variant === 'plain'" class="quantity-input__button-icon" />
      <span v-else>&ndash;</span>
    </button>
    <input
      v-model.number="inputValue"
      :disabled="disabled"
      :min="min"
      type="number"
      class="quantity-input__input"
      :class="{ 'quantity-input__input--wide': inputValue > 99 }"
      @input="debouncedOnQuantityChange('update:modelValue', $event.target.valueAsNumber)"
      @focus="$emit('focus')"
      @blur="onBlur"
    />
    <button
      :disabled="disabled"
      type="button"
      class="quantity-input__button quantity-input__button--right"
      @click="onQuantityChange('add')"
    >
      <IconPlusCircle v-if="variant === 'plain'" class="quantity-input__button-icon" />
      <span v-else>+</span>
    </button>
  </div>
</template>
<script>
import { debounce } from '@/lib/utils/debounce'

import IconMinusCircle from '@/components/common/icons/IconMinusCircle.vue'
import IconPlusCircle from '@/components/common/icons/IconPlusCircle.vue'

export default {
  name: 'QuantityInput',
  components: {
    IconMinusCircle,
    IconPlusCircle,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Number, String],
      required: true,
    },
    alignment: {
      type: String,
      default: 'center',
    },
    step: {
      type: Number,
      default: () => 1,
    },
    min: {
      type: Number,
      default: () => 1,
    },
    max: {
      type: [Number, null],
      default: () => null,
    },
    separateEvents: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: '',
    },
    inputDebounceTime: {
      type: Number,
      default: 600,
    },
  },
  emits: ['focus', 'blur', 'update:modelValue', 'add', 'subtract'],
  data() {
    return {
      inputValue: this.min,
      debouncedOnQuantityChange: () => {},
    }
  },
  computed: {
    classList() {
      let alignmentClass = ''
      let variantClass = ''
      switch (this.alignment) {
        case 'start':
          alignmentClass = 'quantity-input--flex-start'
          break
        default:
          alignmentClass = ''
      }
      switch (this.variant) {
        case 'plain':
          variantClass = 'quantity-input--plain'
          break
        case 'vertical':
          variantClass = 'quantity-input--vertical'
          break
        default:
          variantClass = ''
      }
      return [alignmentClass, variantClass]
    },
    isStepInteger() {
      return Number.isInteger(this.step)
    },
  },
  watch: {
    modelValue(val) {
      this.inputValue = val
    },
  },
  created() {
    this.debouncedOnQuantityChange = debounce((operationType, inputValue) => {
      this.onQuantityChange(operationType, inputValue)
    }, this.inputDebounceTime) // Passing props as fn parameter isn't possible in a method declaration
    this.inputValue = this.modelValue
  },
  methods: {
    onQuantityChange(operationType, inputValue) {
      const oldValue =
        typeof this.modelValue === 'number' && !isNaN(this.modelValue) ? +this.modelValue : this.min
      let newValue =
        operationType === 'add'
          ? oldValue + this.step
          : operationType === 'subtract'
            ? oldValue - this.step
            : isNaN(inputValue)
              ? NaN
              : inputValue
      const eventType = this.separateEvents ? operationType : 'update:modelValue'
      if (isNaN(newValue)) {
        this.inputValue = ''
        this.$emit(eventType, '')
        return
      }
      if (newValue < this.min) newValue = this.min
      if (typeof this.max === 'number' && newValue > this.max) newValue = this.max
      newValue = this.isStepInteger ? Math.floor(newValue) : +newValue.toFixed(1)
      this.inputValue = newValue
      this.$emit(eventType, newValue)
    },
    onBlur() {
      if (typeof this.inputValue !== 'number')
        this.debouncedOnQuantityChange('update:modelValue', this.min)
      this.$emit('blur')
    },
  },
}
</script>
